import { map } from "lodash";
import { aiccInstance } from "utils/instance";
import { AICC_PROMPT_RESET_TARGET, AiccPromptGroup, AiccPromptResetTarget } from "constants/aiccPrompt";
import { AiccLanguageStatus } from "constants/aiccLanguage";
import {
  AiccPromptDetailResponse,
  AiccPromptListResponse,
  AiccPromptModelsResponse,
} from "types/dto/response/aiccPrompt";
import { SaveAiccPromptRequest } from "types/dto/request/aiccPrompt";
import { transformAiccPrompt, transformAiccPromptList, transformAiccPromptModel } from "types/transformers/aiccPrompt";

/**
 * Prompt management (AICC2-60 / AICC2-328), against `{AICC_API_BASE_URL}`, whose base URL already
 * carries `/api/admin`. Backend cards AICC2-526 / 527.
 *
 * `aiccInstance` toasts its own failures, so nothing here catches to report one. What callers do have
 * to handle is the meaning of a 409 on the two write paths: the draft moved, or there is no draft to
 * write into. Both are recoverable and neither is a bug.
 */

/**
 * Rows for one tab of the dashboard, or every prompt when no group is asked for.
 *
 * 🔴 An unrecognised group is a 400, not an empty list — "this tab has no prompts" and "there is no
 * such tab" are different answers — so the caller passes a group from the constant, never a raw slug.
 */
export async function fetchAiccPrompts(languageId: number, group?: AiccPromptGroup) {
  return aiccInstance
    .get<AiccPromptListResponse>(`/languages/${languageId}/prompts`, { params: group ? { group } : undefined })
    .then((response) => transformAiccPromptList(response));
}

/**
 * One prompt with its variables, its default and which copy its text came from.
 *
 * 🔴 `status` NAMES WHICH COPY IS BEING ASKED ABOUT, and leaving it off is how the badge came to read
 * Preview-Ready on a published page (AICC2-571). A language can serve a version AND hold a draft at the
 * same time, so the list gives that prompt two rows pointing at this one endpoint; without the parameter
 * the backend answers for the draft, because only the caller knows which row was clicked.
 *
 * It changes the `status` field alone — all three configs come back either way, since the page renders the
 * published copy beside the editor.
 *
 * 🔴 ABSENT RATHER THAN EMPTY. The backend 400s an empty string, and omitting it has to leave the request
 * byte-for-byte what it was before this parameter existed.
 */
export async function fetchAiccPrompt(languageId: number, promptKey: string, status?: AiccLanguageStatus) {
  return aiccInstance
    .get<AiccPromptDetailResponse>(`/languages/${languageId}/prompts/${promptKey}`, {
      params: status ? { status } : undefined,
    })
    .then((response) => transformAiccPrompt(response));
}

/**
 * Writes into the language's draft, and only the fields that changed.
 *
 * 🔴 PARTIAL ON PURPOSE. A model configuration can drive two prompts — Hint and Lesson Review — Hint
 * are one record — so sending a whole payload back would overwrite the other one with whatever this
 * screen last read. The caller builds the diff; this only carries it.
 *
 * A 409 means the draft moved since it was read, or the language has no draft at all. The response
 * message names the way out, and `aiccInstance` has already shown it.
 */
export async function saveAiccPrompt(languageId: number, promptKey: string, payload: SaveAiccPromptRequest) {
  return aiccInstance
    .put<AiccPromptDetailResponse>(`/languages/${languageId}/prompts/${promptKey}`, payload)
    .then((response) => transformAiccPrompt(response));
}

/**
 * Puts one prompt's text back (AICC2-526): to the base set every language shares, or to this language's
 * own published version.
 *
 * 🔴 THIS PROMPT, AND ONLY ITS TEXT. It used to delete the whole flow record, so resetting Hint reset
 * Lesson Review — Hint with it and dropped the model and temperature the flow was running on. Neither
 * happens now — which is why the confirmation no longer names siblings, and why the model warning
 * (`confirmSharedSettingChange`) is still the only place sharing is mentioned.
 *
 * A 409 covers three states at once — no open draft, somebody else just wrote this prompt, or the target
 * scope has no text for it (a prompt added after the published version was frozen). They are not
 * distinguishable from here, so the backend's own message is what gets shown; `aiccInstance` has already
 * shown it by the time this rejects.
 */
export async function resetAiccPrompt(
  languageId: number,
  promptKey: string,
  target: AiccPromptResetTarget = AICC_PROMPT_RESET_TARGET.default
) {
  return aiccInstance
    .post<AiccPromptDetailResponse>(`/languages/${languageId}/prompts/${promptKey}/reset`, undefined, {
      params: { target },
    })
    .then((response) => transformAiccPrompt(response));
}

/**
 * The models the dropdown offers, with their prices and the reasoning efforts each accepts.
 *
 * Global, not per language, which is why it is read once and shared rather than carried on every
 * prompt.
 */
export async function fetchAiccPromptModels() {
  return aiccInstance
    .get<AiccPromptModelsResponse>("/llm/models")
    .then((response) => map(response?.models ?? [], transformAiccPromptModel));
}
